/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable jsx-a11y/anchor-is-valid */

import React, { createRef } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import T from 'i18n-react';
import Link from 'next/link';
import Router from 'next/router';

import {
	FeedbackTooltip,
	Icons,
	LabelContainer,
	LoadingSpinner,
	MultiSelect,
	Spacer,
	Textarea,
	withViewportProvider,
} from '@motorway/motorway-component-library';
import { ProgressStepsIndicator } from '@motorway/motorway-storybook-cra';
import { Button } from '@motorway/mw-highway-code';

import cypressIds from 'Components/cypress_ids.json';
import Content from 'Storybook/local/Content/Content';

import { applyCypressData, isUndefined, scrollIntoView } from '../../utilities/helpers';
import LoaderWithText from '../Loading/LoadingWithText';

import {
	companyTypeOptions,
	distanceOptions,
	FORM_STEPS,
	howHearOptions,
	positionOptions,
	STATUS,
	vehiclesPerMonthOptions,
} from './SignupPageConsts';
import SigupPageFormBase from './SigupPageBackend';

import styles from './SignupPage.module.scss';

T.setTexts(require('../texts.json'));

/**
 * This component contains Signup page presentation layer
 */
class SignupPageFrontend extends SigupPageFormBase {
	wrapperRef = createRef();

	constructor(props) {
		super(props);

		const { index, makes } = props;
		const formSteps = Object.keys(FORM_STEPS);
		const isSSR = !process.browser;

		this.defaultValues = {
			buysAllMakes: null,
			carsPerMonth: null,
			companyType: null,
			howDidYouHearAboutUs: null,
			legal: false,
			makes: [],
			maximumCollectionDistance: null,
		};

		this.rangeFields = ['price', 'age', 'mileage'];
		this.rangeFromFields = this.rangeFields.map((name) => `${name}From`);
		this.rangeToFields = this.rangeFields.map((name) => `${name}To`);

		[...this.rangeFromFields, ...this.rangeToFields].forEach((field) => {
			this.defaultValues[field] = null;
		});

		this.fields = {
			[FORM_STEPS.DEALER]: [
				'contactName',
				'name',
				'contactPosition',
				'contactPhone',
				'email',
				'howDidYouHearAboutUs',
				'howDidYouHearAboutUsOtherDescription',
				'legal',
			],
			[FORM_STEPS.DEALERSHIP]: [
				'registrationNumber',
				'address1',
				'address2',
				'address3',
				'city',
				'postcode',
				'companyType',
				'website',
				'companyPhone',
			],
			[FORM_STEPS.REQUIREMENTS]: [
				'makes',
				'allMakes',
				'buysAllMakes',
				'carsPerMonth',
				'additionalInfo',
				'maximumCollectionDistance',
				...this.rangeFromFields,
				...this.rangeToFields,
			],
		};

		this.optionalFields = ['address2', 'address3', 'allMakes', 'additionalInfo'];

		this.INITIAL_STATE = {
			dealerID: null,
			formStep: formSteps[index],
			formSteps,
			historyLog: !isSSR ? window.history.length : 0,
			index,
			inputs: [].concat(...Object.values(this.fields)).map((name) => ({
				focused: false,
				name,
				required: !this.optionalFields.includes(name),
				showIfInvalid: false,
				valid: this.optionalFields.includes(name),
				value: isUndefined(this.defaultValues[name]) ? '' : this.defaultValues[name],
			})),
			makesList: makes,
			progressStepsIndicator: {
				steps: [
					{
						completed: false,
						id: FORM_STEPS.DEALER,
						name: 'Your details',
					},
					{
						completed: false,
						id: FORM_STEPS.DEALERSHIP,
						name: 'Dealership details',
					},
					{
						completed: false,
						id: FORM_STEPS.REQUIREMENTS,
						name: 'Purchasing requirements',
					},
				],
			},
			purchaseAllMakers: null,
			sending: false,
			status: STATUS.DEFAULT,
		};

		this.state = { ...this.INITIAL_STATE };

		this.$form = React.createRef();
		this.textsPrefix = 'signupPage';

		if (!isSSR) {
			window.addEventListener('popstate', this.onPopStateChange, false);
			Router.replace({
				pathname: '/signup',
				query: { step: index + 1 }, // Display step as one above index.
			});
		}
	}

	onNumInputKeyUp = ({ target: { id, value } }) => this.updateInputState(id, { value: this.getInputFormat(id, value) });

	getSubmitButton = ({ cypressId, step, textKey = 'signupPage.nextStep' }) => {
		const { sending } = this.state;

		const props = {
			...(cypressId && applyCypressData(cypressId)),
		};
		const child = [];

		if (sending) {
			props.disabled = true;
			child.push(<LoadingSpinner key="loading" className={styles.buttonSpinner} />);
		} else {
			if (step) {
				props.onClick = () => this.goToNextStep(step);
			}
			props.rightIcon = 'chevron-right';
			child.push(T.translate(textKey));
		}

		return <Button fullWidth type="submit" {...props} label={child} />;
	};

	getPersonalDetailsSection = () => (
		<section className={styles.formSection}>
			<h3 className={cx('mw-title-title3', styles.headerBox)}>
				<Icons.PersonalDetailsSVG className={styles.icon} fill="#ffffff" height="32" width="32" />
				{T.translate('signupPage.personalDetailsSection.title')}
			</h3>

			{this.getTextInput({
				cypressId: cypressIds.forms.signUp.fullNameInput,
				label: T.translate('signupPage.personalDetailsSection.fullName'),
				name: 'contactName',
				type: 'text',
			})}

			{this.getTextInput({
				cypressId: cypressIds.forms.signUp.dealershipNameInput,
				label: T.translate('signupPage.personalDetailsSection.dealershipName'),
				name: 'name',
				type: 'text',
			})}

			{this.getSelectInput({
				cypressId: cypressIds.forms.signUp.contactPositionSelect,
				label: T.translate('signupPage.personalDetailsSection.contactPosition'),
				name: 'contactPosition',
				options: positionOptions,
				placeholder: T.translate('signupPage.personalDetailsSection.contactPositionDefaultOption'),
			})}

			{this.getTextInput({
				cypressId: cypressIds.forms.signUp.mobilePhoneInput,
				inputProps: {
					inputMode: 'numeric',
					pattern: '[0-9]*',
					style: {
						textAlign: 'left',
					},
				},
				label: T.translate('signupPage.personalDetailsSection.mobile'),
				name: 'contactPhone',
				prefix: '+44',
				type: this.state.validatingPhone ? 'loading' : 'text',
			})}

			{this.getTextInput({
				cypressId: cypressIds.forms.signUp.emailInput,
				inputProps: {
					pattern: '[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+[.][a-zA-Z]{2,}$',
				},
				label: T.translate('signupPage.personalDetailsSection.email'),
				name: 'email',
				type: this.state.validatingEmail ? 'loading' : 'email',
			})}

			{this.getSelectInput({
				cypressId: cypressIds.forms.signUp.howDidYouHearAboutUsSelect,
				label: T.translate('signupPage.personalDetailsSection.howDidYouHearAboutUs'),
				name: 'howDidYouHearAboutUs',
				options: howHearOptions,
				placeholder: T.translate('signupPage.personalDetailsSection.howDidYouHearAboutUsDefaultOption'),
			})}

			{this.getValue('howDidYouHearAboutUs') === 'Other' &&
				this.getTextareaInput({
					name: 'howDidYouHearAboutUsOtherDescription',
				})}

			<Spacer size="small" />

			{this.getCheckboxInput({
				className: styles.legal,
				cypressId: cypressIds.forms.signUp.legalCheckbox,
				label: T.translate('signupPage.legal.text', {
					privacy: (
						<a href="/privacy" rel="noopener noreferrer" target="_blank">
							{T.translate('signupPage.legal.privacy')}
						</a>
					),
					terms: (
						<a className={styles.termsLink} href="/terms" rel="noopener noreferrer" target="_blank">
							{T.translate('signupPage.legal.terms')}
						</a>
					),
				}),
				name: 'legal',
			})}

			<Spacer size="small" />

			{this.getSubmitButton({
				cypressId: cypressIds.forms.signUp.nextStepButton,
				step: FORM_STEPS.DEALERSHIP,
			})}
		</section>
	);

	getDealershipDetails = () => (
		<section className={styles.formSection}>
			<h3 className={cx('mw-title-title3', styles.headerBox)}>
				<Icons.DealershipSVG className={styles.icon} fill="#ffffff" height="32" width="32" />
				{T.translate('signupPage.dealershipDetailsSection.title')}
			</h3>

			{this.getTextInput({
				cypressId: cypressIds.forms.signUp.registrationNumber,
				label: T.translate('signupPage.dealershipDetailsSection.registrationNumber'),
				labelProps: {
					labelAfter: this.getTooltip('registrationNumber'),
				},
				name: 'registrationNumber',
				type: 'text',
			})}

			<span className={styles.addressBlock}>
				{this.getTextInput({
					cypressId: cypressIds.forms.signUp.address1Input,
					label: T.translate('signupPage.dealershipDetailsSection.dealershipAddress'),
					name: 'address1',
					placeholder: T.translate('signupPage.dealershipDetailsSection.address1Placeholder'),
					type: 'text',
				})}

				{this.getTextInput({
					cypressId: cypressIds.forms.signUp.address2Input,
					label: T.translate('signupPage.dealershipDetailsSection.address2'),
					name: 'address2',
					placeholder: T.translate('signupPage.dealershipDetailsSection.address2Placeholder'),
					showLabel: false,
					type: 'text',
				})}

				{this.getTextInput({
					cypressId: cypressIds.forms.signUp.address3Input,
					label: T.translate('signupPage.dealershipDetailsSection.address3'),
					name: 'address3',
					placeholder: T.translate('signupPage.dealershipDetailsSection.address3Placeholder'),
					showLabel: false,
					type: 'text',
				})}

				{this.getTextInput({
					cypressId: cypressIds.forms.signUp.cityInput,
					label: T.translate('signupPage.dealershipDetailsSection.city'),
					name: 'city',
					placeholder: T.translate('signupPage.dealershipDetailsSection.city'),
					showLabel: false,
					type: 'text',
				})}

				{this.getTextInput({
					className: styles.postcode,
					cypressId: cypressIds.forms.signUp.postcodeInput,
					label: T.translate('signupPage.dealershipDetailsSection.postcode'),
					name: 'postcode',
					placeholder: T.translate('signupPage.dealershipDetailsSection.postcodePlaceholder'),
					showLabel: false,
					type: 'text',
				})}
			</span>

			{this.getSelectInput({
				cypressId: cypressIds.forms.signUp.companyTypeSelect,
				label: T.translate('signupPage.dealershipDetailsSection.companyType'),
				name: 'companyType',
				options: companyTypeOptions,
				placeholder: T.translate('signupPage.dealershipDetailsSection.companyTypeDefaultOption'),
			})}

			{this.getTextInput({
				cypressId: cypressIds.forms.signUp.websiteInput,
				label: T.translate('signupPage.dealershipDetailsSection.dealershipWebsite'),
				name: 'website',
				placeholder: T.translate('signupPage.dealershipDetailsSection.dealershipWebsitePlaceholder'),
				type: 'text',
			})}

			{this.getTextInput({
				cypressId: cypressIds.forms.signUp.companyPhoneInput,
				inputProps: {
					inputMode: 'numeric',
					pattern: '[0-9]*',
					style: {
						textAlign: 'left',
					},
				},
				label: T.translate('signupPage.dealershipDetailsSection.companyPhone'),
				name: 'companyPhone',
				type: this.state.validatingPhone ? 'loading' : 'text',
			})}

			<Spacer size="base" />

			{this.getSubmitButton({
				cypressId: cypressIds.forms.signUp.nextStepButton,
				step: FORM_STEPS.REQUIREMENTS,
			})}
		</section>
	);

	getPurchaseRequirements = () => {
		const { makesList, purchaseAllMakers } = this.state;

		const allMakesClassName = cx('mw-radio', {
			'mw-input-error': this.isErrorIntent('buysAllMakes'),
		});

		const specificMakesClassName = cx('mw-radio', {
			[styles.makes]: purchaseAllMakers || purchaseAllMakers === null,
			'mw-input-error': this.isErrorIntent('makes'),
		});

		const multiselectValue = this.getValue('makes').map((v) => makesList.find((m) => m.value === v));
		const multiselectIntent = this.isErrorIntent('makes') ? 'error' : null;

		return (
			<section className={styles.formSection}>
				<h3 className={cx('mw-title-title3', styles.headerBox)}>
					<Icons.AssignmentSVG className={styles.icon} fill="#ffffff" height="32" width="32" />
					{T.translate('signupPage.purchasingRequirementsSection.title')}
				</h3>
				<FeedbackTooltip
					appendTo={this.wrapperRef}
					content={this.getErrorMessage('buysAllMakes')}
					intent={this.getInputIntent('buysAllMakes')}
				>
					<div ref={this.wrapperRef} id="makes-section">
						<LabelContainer
							className={styles.makesLabel}
							label={this.getLabel({
								label: T.translate('signupPage.purchasingRequirementsSection.whichMakes'),
								name: 'makes',
							})}
							labelAfter={this.getTooltip('makes')}
						/>
						<div className={allMakesClassName}>
							<input
								checked={purchaseAllMakers === true}
								className={styles.radio}
								id="buysAllMakes"
								name="buysAllMakes"
								onChange={this.onPurchaseAllMakersChange}
								type="radio"
								value="1"
								{...applyCypressData(cypressIds.forms.signUp.allMakesRadio)}
							/>
							<label htmlFor="buysAllMakes">{T.translate('signupPage.purchaseVehiclesControl.allMakers')}</label>
						</div>
						<div className={specificMakesClassName}>
							<input
								checked={purchaseAllMakers === false}
								className={styles.radio}
								id="makes"
								name="buysAllMakes"
								onChange={this.onPurchaseAllMakersChange}
								type="radio"
								value="0"
								{...applyCypressData(cypressIds.forms.signUp.makesRadio)}
							/>
							<label htmlFor="makes">{T.translate('signupPage.purchaseVehiclesControl.specificMakers')}</label>
						</div>
						{purchaseAllMakers === false && (
							<MultiSelect
								searchBar
								feedbackTooltip={{ content: this.getErrorMessage('makes') }}
								id="multiselect"
								inputProps={{ ...applyCypressData(cypressIds.forms.signUp.makesMultiSelect) }}
								intent={multiselectIntent}
								name="makes"
								onChange={this.onMultiselectChange}
								options={makesList}
								placeholder={T.translate('signupPage.purchaseVehiclesControl.multiselectPlaceholder')}
								value={multiselectValue}
							/>
						)}
					</div>
				</FeedbackTooltip>
				<div className="range-field">
					<LabelContainer
						className={styles.rangeLabel}
						label={this.getLabel({
							label: T.translate('signupPage.purchasingRequirementsSection.whatValue'),
							name: 'priceFrom',
						})}
						labelAfter={this.getTooltip('price')}
					/>

					<div className={styles.rangeBox}>
						{this.getTextInput({
							cypressId: cypressIds.forms.signUp.priceFromInput,
							inputProps: {
								onKeyUp: this.onNumInputKeyUp,
								pattern: '[0-9]*',
							},
							money: true,
							name: 'priceFrom',
							placeholder: '4,000',
							type: 'text',
						})}
						<div className={styles.rangeBoxSeparator}>-</div>
						{this.getTextInput({
							cypressId: cypressIds.forms.signUp.priceToInput,
							inputProps: {
								onKeyUp: this.onNumInputKeyUp,
								pattern: '[0-9]*',
							},
							money: true,
							name: 'priceTo',
							placeholder: '10,000',
							type: 'text',
						})}
					</div>
				</div>

				<div className="range-field">
					<LabelContainer
						className={styles.rangeLabel}
						label={this.getLabel({
							label: T.translate('signupPage.purchasingRequirementsSection.whatAge'),
							name: 'ageFrom',
						})}
						labelAfter={this.getTooltip('age')}
					/>

					<div className={styles.rangeBox}>
						{this.getTextInput({
							cypressId: cypressIds.forms.signUp.rangeFromInput,
							inputProps: {
								onKeyUp: this.onNumInputKeyUp,
								pattern: '[0-9]*',
							},
							name: 'ageFrom',
							placeholder: '1',
							prefix: T.translate('signupPage.purchasingRequirementsSection.moneyFromPlaceholder'),
							type: 'text',
						})}
						<div className={styles.rangeBoxSeparator}>-</div>
						{this.getTextInput({
							cypressId: cypressIds.forms.signUp.rangeToInput,
							inputProps: {
								onKeyUp: this.onNumInputKeyUp,
								pattern: '[0-9]*',
							},
							name: 'ageTo',
							placeholder: '5',
							prefix: T.translate('signupPage.purchasingRequirementsSection.moneyToPlaceholder'),
							type: 'text',
						})}
					</div>
				</div>

				<div className="range-field">
					<LabelContainer
						className={styles.rangeLabel}
						label={this.getLabel({
							label: T.translate('signupPage.purchasingRequirementsSection.whatMileage'),
							name: 'mileageFrom',
						})}
						labelAfter={this.getTooltip('mileage')}
					/>

					<div className={styles.rangeBox}>
						{this.getTextInput({
							cypressId: cypressIds.forms.signUp.mileageFromInput,
							inputProps: {
								onKeyUp: this.onNumInputKeyUp,
								pattern: '[0-9]*',
							},
							name: 'mileageFrom',
							placeholder: '12,000',
							prefix: T.translate('signupPage.purchasingRequirementsSection.moneyFromPlaceholder'),
							type: 'text',
						})}
						<div className={styles.rangeBoxSeparator}>-</div>
						{this.getTextInput({
							cypressId: cypressIds.forms.signUp.mileageToInput,
							inputProps: {
								onKeyUp: this.onNumInputKeyUp,
								pattern: '[0-9]*',
							},
							name: 'mileageTo',
							placeholder: '20,000',
							prefix: T.translate('signupPage.purchasingRequirementsSection.moneyToPlaceholder'),
							type: 'text',
						})}
					</div>
				</div>

				<div className="select-fields">
					{this.getSelectInput({
						cypressId: cypressIds.forms.signUp.distanceSelect,
						label: T.translate('signupPage.purchasingRequirementsSection.distance'),
						name: 'maximumCollectionDistance',
						options: distanceOptions,
						placeholder: T.translate('signupPage.purchasingRequirementsSection.distanceDefaultOption'),
					})}

					{this.getSelectInput({
						cypressId: cypressIds.forms.signUp.vehiclesPerMonthSelect,
						label: T.translate('signupPage.purchasingRequirementsSection.vehiclesEachMonth'),
						name: 'carsPerMonth',
						options: vehiclesPerMonthOptions,
						placeholder: T.translate('signupPage.purchasingRequirementsSection.vehiclesEachMonthDefaultOption'),
					})}
				</div>

				<div className="textarea-field">
					<Textarea
						id="additionalInfo"
						label={<LabelContainer label={T.translate('signupPage.purchasingRequirementsSection.anythingElse')} />}
						name="additionalInfo"
						textareaProps={{
							id: 'additionalInfo',
							onBlur: this.onBlur,
							onInput: this.onChange,
							placeholder: '',
						}}
					/>
				</div>

				<Spacer size="small" />

				{this.getSubmitButton({
					cypressId: cypressIds.forms.signUp.nextStepButton,
					textKey: 'signupPage.submit',
				})}
			</section>
		);
	};

	// eslint-disable-next-line class-methods-use-this
	getLoader = (message) => (
		<div className={cx(styles.container, styles.messageBox)}>
			<LoaderWithText message={message} />
		</div>
	);

	// eslint-disable-next-line class-methods-use-this
	getThankYouView = () => {
		const phone = T.translate('contactInfo.phone');

		return (
			<div className={cx(styles.container, styles.messageBox)}>
				<h1 className={styles.messageHeader}>{T.translate('signupPage.thankYouSection.title')}</h1>
				<p className={styles.messageText}>
					{T.translate('signupPage.thankYouSection.text', {
						begin: <span>{T.translate('signupPage.thankYouSection.begin')}</span>,
						phone: (
							<a className={cx('mw-link', styles.phone)} href={`tel:${phone}`}>
								{phone}
							</a>
						),
					})}
				</p>
				<Link legacyBehavior passHref as="/" href="/">
					<Button
						as="a"
						icon="chevron"
						{...applyCypressData(cypressIds.forms.signUp.backToHomeButton)}
						label={T.translate('signupPage.thankYouSection.button')}
					/>
				</Link>
				<div className={styles.spacer} />
			</div>
		);
	};

	getErrorView = () => {
		const phone = T.translate('contactInfo.phone');

		return (
			<div className={cx(styles.container, styles.messageBox)}>
				<h1 className={styles.messageHeader}>{T.translate('signupPage.errorSection.title')}</h1>
				<p className={styles.errorText}>
					{T.translate('signupPage.errorSection.text', {
						phone: (
							<a className={cx('mw-link', styles.phone)} href={`tel:${phone}`}>
								{phone}
							</a>
						),
					})}
				</p>
				<Button icon="chevron" label={T.translate('signupPage.errorSection.button')} onClick={() => this.resetForm()} />
				<div className={styles.spacer} />
			</div>
		);
	};

	getFormViewContext = () => {
		const { formStep } = this.state;

		switch (formStep) {
			case FORM_STEPS.DEALERSHIP:
				return this.getDealershipDetails();
			case FORM_STEPS.REQUIREMENTS:
				return this.getPurchaseRequirements();
			default:
				return this.getPersonalDetailsSection();
		}
	};

	getFormView = () => {
		const { index, progressStepsIndicator } = this.state;
		const { steps } = progressStepsIndicator;

		return (
			<>
				<form
					key={this.state.formStep}
					ref={this.$form}
					noValidate
					className={styles.container}
					onSubmit={this.onSubmit}
				>
					<ProgressStepsIndicator index={index} steps={steps} />
					{this.getFormViewContext()}
				</form>
			</>
		);
	};

	getViewContext = () => {
		const { makesList, status } = this.state;

		if (!makesList.length) {
			return this.getLoader();
		}

		switch (status) {
			case STATUS.SENDING:
				return this.getLoader(T.translate('signupPage.submitting'));
			case STATUS.ERROR:
				return this.getErrorView();
			case STATUS.SEND:
				return this.getThankYouView();
			default:
				return this.getFormView();
		}
	};

	componentDidUpdate(prevProps, prevState) {
		if (this.state.formStep !== prevState.formStep) {
			scrollIntoView(document.body);
		}
	}

	componentWillUnmount() {
		window.removeEventListener('popstate', this.onPopStateChange, false);
	}

	render = () => {
		const { status } = this.state;
		const hide = ![STATUS.DEFAULT, STATUS.EMAIL_EXISTS].includes(status);

		return (
			<div className={styles.root}>
				<Content className={cx(styles.content)} id="signup-content" wrapper={{ className: styles.contentWrapper }}>
					<h1 className={cx(styles.header)}>
						{T.translate('signupPage.header', {
							back: <span>{T.translate('signupPage.headerBack')}</span>,
							front: <span className={styles.signInMessageText}>{T.translate('signupPage.headerFront')}</span>,
						})}
					</h1>
					<p className={cx(styles.signInMessage, { [styles.signInMessageHide]: hide })}>
						{T.translate('signupPage.signInMessage')}
					</p>
					{this.getViewContext()}
				</Content>
			</div>
		);
	};
}

SignupPageFrontend.defaultProps = {
	index: 0,
};

SignupPageFrontend.propTypes = {
	formStep: PropTypes.string,
	makes: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number,
			name: PropTypes.string,
			total: PropTypes.number,
		}),
	).isRequired,
};

export default withViewportProvider(SignupPageFrontend);
